/**
 * Helpers puros de ubicación para navegación (QR del repartidor, mensajes al grupo de
 * motomandados, enlaces que se copian a WhatsApp).
 *
 * Sprint 76 — Vivían en `main/services/despachoMapa.ts`, que sólo alcanzaba el proceso main.
 * El mensaje de cotización que se copia al portapapeles se arma en `shared` (lo usan el
 * renderer del escritorio y el del track web), y necesita exactamente las mismas reglas de
 * whitelist de hosts: duplicarlas habría sido garantizar que un día divergieran, y lo que aquí
 * se decide es a dónde va a manejar una persona.
 *
 * `main/services/despachoMapa.ts` las reexporta, así que el código y los tests del Sprint 8
 * siguen importándolas de donde siempre.
 */

/**
 * Construye la URL del QR para navegación.
 * Prioriza coords (más exactas que dirección textual). Fallback a búsqueda
 * por dirección + "Villahermosa, Tabasco" para sesgar al mercado del cliente alpha.
 *
 * RN-S8-07: URL universal Google Maps. Razón: `geo:` URI falla en iOS;
 * la URL universal abre browser → "Abrir en Maps/Waze" en cualquier phone.
 */
export function buildMapaUrl(p: {
  clienteLatitud?: number | null;
  clienteLongitud?: number | null;
  clienteDireccion?: string | null;
}): string {
  if (p.clienteLatitud != null && p.clienteLongitud != null) {
    return `https://www.google.com/maps/search/?api=1&query=${p.clienteLatitud},${p.clienteLongitud}`;
  }

  /**
   * Sprint 65 — Si la "dirección" es un enlace de mapa, ése ES el destino.
   *
   * Pasaba de verdad: el operador pegaba en el campo de dirección el enlace que el cliente le
   * mandó por WhatsApp, se guardaba tal cual y aquí se armaba una búsqueda de Google **con la
   * URL como texto de calle**. Google aterrizaba donde podía y el repartidor iba a otra parte
   * — el defecto que reportó el negocio como "la presentan en un lugar equivocado".
   *
   * El formulario ya no deja que eso vuelva a guardarse, pero los pedidos que ya están en la
   * base sí lo tienen, y para ésos esto los arregla: el QR lleva al pin del cliente.
   */
  const enlace = enlaceDeMapaEn(p.clienteDireccion);
  if (enlace) return enlace;

  const partes = [p.clienteDireccion, 'Villahermosa, Tabasco'].filter(
    (s): s is string => Boolean(s && s.trim()),
  );
  const q = encodeURIComponent(partes.join(', '));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/** Hosts cuyos enlaces sirven como destino de navegación tal cual. */
const HOSTS_MAPA = [
  'maps.app.goo.gl',
  'goo.gl',
  'g.co',
  'maps.google.com',
  'www.google.com',
  'google.com',
];

/**
 * Devuelve el primer enlace de Google Maps que haya en el texto, o `null`.
 *
 * Whitelist de hosts, igual que el resolvedor de enlaces: un QR impreso es algo que alguien va
 * a escanear con su teléfono, así que no se convierte en destino cualquier URL que haya
 * acabado en el campo de dirección.
 */
export function enlaceDeMapaEn(texto?: string | null): string | null {
  if (!texto) return null;
  const m = texto.match(/https?:\/\/[^\s]+/i);
  if (!m) return null;
  try {
    const u = new URL(m[0]);
    const host = u.hostname.toLowerCase();
    if (!HOSTS_MAPA.includes(host)) return null;
    // De google.com sólo valen las rutas de mapas: `google.com/search?q=…` no es un destino.
    if (host.endsWith('google.com') && !/\/maps/.test(u.pathname)) return null;
    return m[0];
  } catch {
    return null;
  }
}

/**
 * RN-S8-06: el QR NO debe contener PII (nombre, teléfono, dirección textual).
 * Esta función verifica que la URL de mapa cumpla con esa regla.
 * Retorna true si la URL es segura para imprimir en QR.
 */
export function qrSinPII(url: string): boolean {
  // Sólo deben aparecer dígitos, símbolos de coords o cadenas inocuas.
  // Si el query lleva nombre/telefono, está mal.
  const queryStart = url.indexOf('query=');
  if (queryStart === -1) return true;
  const query = decodeURIComponent(url.slice(queryStart + 6));
  // Permitido: "LAT,LNG" o cadena de dirección (calle/colonia/estado).
  // No permitido: emails, teléfonos sueltos, "Pedido #" con datos personales.
  return !/(@|[Tt]el[eé]fono|cliente|nombre)/i.test(query);
}

/**
 * Sprint 76 — ¿Este texto lleva **coordenadas** y nada más que coordenadas?
 *
 * Es la comprobación que necesita el mensaje que se copia al grupo de motomandados: ahí sólo
 * puede viajar el punto, nunca la calle escrita —que en Villahermosa suele traer el nombre de
 * quien vive ahí— ni el enlace corto original, que al abrirlo muestra la dirección completa.
 */
export function esUrlDeCoordenadas(url: string): boolean {
  const m = url.match(/[?&]query=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/);
  if (!m) return false;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}
